/**
 * The compiled `AppType` descriptor of a project's type, for the core views
 * that name or address what its chart deploys — pod names, the future
 * address of an ephemeral branch, the promotion dialog (genericity plan,
 * step 6, 2026-10-24) —, compiled with the same engine as the backend's
 * (`waadoo-config-common`).
 *
 * Read through the descriptors' cache of `templateRoles.ts` (`loadAppType`,
 * one request per type every 30 s for the whole app). By the rule of
 * `appTitle.ts`: for an Odoo project (`LEGACY_APP_TYPE`), and for a project
 * whose type is not known yet (undefined), the embedded Odoo descriptor
 * while the descriptor loads, and for good when it cannot be read or does
 * not compile — an Odoo view never shows another naming first. For another
 * type, undefined until its descriptor is read, and for good when it cannot
 * be: the callers then name and guess less.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  compileDescriptor,
  embeddedOdooDescriptor,
  type AppTypeDescriptor,
} from '@internal/plugin-waadoo-config-common';
import type { AppTypeDetail } from './appTypes';
import { LEGACY_APP_TYPE } from './projectIdentity';
import { loadAppType } from './templateRoles';

/** One compilation per answer of the cache (the same object for 30 s). */
const compiled = new WeakMap<AppTypeDetail, AppTypeDescriptor | null>();

/** `detail` compiled; undefined when the engine refuses it. */
export function compiledDescriptorOf(
  detail: AppTypeDetail,
): AppTypeDescriptor | undefined {
  let descriptor = compiled.get(detail);
  if (descriptor === undefined) {
    try {
      descriptor = compileDescriptor({
        name: detail.name,
        title: detail.title,
        spec: detail.spec,
      });
    } catch {
      // A descriptor the engine refuses: as one that cannot be read.
      descriptor = null;
    }
    compiled.set(detail, descriptor);
  }
  return descriptor ?? undefined;
}

/** What stands for the descriptor of `appType` without one (header). */
export function fallbackDescriptor(
  appType: string | undefined,
): AppTypeDescriptor | undefined {
  return appType === undefined || appType === LEGACY_APP_TYPE
    ? embeddedOdooDescriptor()
    : undefined;
}

/** The compiled descriptor of `appType` (header). */
export function useAppTypeDescriptor(
  appType: string | undefined,
): AppTypeDescriptor | undefined {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [loaded, setLoaded] = useState<{
    name: string;
    detail: AppTypeDetail | undefined;
  }>();

  useEffect(() => {
    if (appType === undefined) return undefined;
    let active = true;
    loadAppType({ fetchApi, discoveryApi }, appType).then(
      detail => {
        if (active) setLoaded({ name: appType, detail });
      },
      () => {
        // Unreadable descriptor: the fallback stays (header).
        if (active) setLoaded({ name: appType, detail: undefined });
      },
    );
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi, appType]);

  const detail = loaded?.name === appType ? loaded?.detail : undefined;
  return useMemo(
    () =>
      (detail ? compiledDescriptorOf(detail) : undefined) ??
      fallbackDescriptor(appType),
    [detail, appType],
  );
}
